import {normaliseFuelTypes} from '../smokehouse/fuel-types.js';
import {
  CapabilityAnswer,
  TemperatureControl,
  createSmokerCapabilities
} from '../smokehouse/smoker-capabilities.js';
import {createSmoker} from '../smokehouse/smoker.js';

export const SMOKER_SNAPSHOT_VERSION=1;

function requiredText(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(`${label} is required`);
  if(text.length>max)throw new TypeError(`${label} is too long`);
  return text;
}

function optionalStableKey(value,label){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!/^[a-z][a-z0-9_]{1,63}$/.test(key))throw new TypeError(`Invalid ${label}`);
  return key;
}

function isoTimestamp(value,label='capturedAt'){
  const text=String(value||'').trim();
  if(!text||Number.isNaN(Date.parse(text)))throw new TypeError(`${label} must be an ISO timestamp`);
  return text;
}

function version(value){
  const number=Number(value);
  if(number!==SMOKER_SNAPSHOT_VERSION)throw new TypeError('Unsupported smoker snapshot version');
  return number;
}

export function normaliseSmokerSnapshot(value={}){
  if(!value||typeof value!=='object'||Array.isArray(value)){
    throw new TypeError('smoker snapshot must be an object');
  }

  const smokerId=requiredText(value.smokerId,'smokerId',128);
  const capability=createSmokerCapabilities({
    smokerId,
    fuelTypes:value.fuelTypes||[],
    minTemperatureCelsius:value.minTemperatureCelsius,
    maxTemperatureCelsius:value.maxTemperatureCelsius,
    coldSmoking:value.coldSmoking??CapabilityAnswer.UNKNOWN,
    coldSmokeMaxCelsius:value.coldSmokeMaxCelsius,
    temperatureControl:value.temperatureControl??TemperatureControl.UNKNOWN,
    controlPrecisionCelsius:value.controlPrecisionCelsius
  });

  return Object.freeze({
    snapshotVersion:version(value.snapshotVersion??SMOKER_SNAPSHOT_VERSION),
    smokerId,
    name:requiredText(value.name,'name',100),
    smokerTypeKey:optionalStableKey(value.smokerTypeKey,'smokerTypeKey'),
    fuelTypes:normaliseFuelTypes(capability.fuelTypes),
    minTemperatureCelsius:capability.minTemperatureCelsius,
    maxTemperatureCelsius:capability.maxTemperatureCelsius,
    coldSmoking:capability.coldSmoking,
    coldSmokeMaxCelsius:capability.coldSmokeMaxCelsius,
    temperatureControl:capability.temperatureControl,
    controlPrecisionCelsius:capability.controlPrecisionCelsius,
    capturedAt:isoTimestamp(value.capturedAt)
  });
}

export function createSmokerSnapshot({
  smoker,
  capabilities,
  capturedAt=new Date().toISOString()
}={}){
  const equipment=createSmoker(smoker);
  if(!capabilities||typeof capabilities!=='object'){
    throw new TypeError('smoker capabilities are required');
  }
  const capabilityId=String(capabilities.smokerId||capabilities.id||'').trim();
  if(capabilityId&&capabilityId!==equipment.id){
    throw new TypeError('Smoker capability identity mismatch');
  }
  const capability=createSmokerCapabilities({...capabilities,smokerId:equipment.id});

  return normaliseSmokerSnapshot({
    snapshotVersion:SMOKER_SNAPSHOT_VERSION,
    smokerId:equipment.id,
    name:equipment.name,
    smokerTypeKey:equipment.smokerTypeKey,
    fuelTypes:[...capability.fuelTypes],
    minTemperatureCelsius:capability.minTemperatureCelsius,
    maxTemperatureCelsius:capability.maxTemperatureCelsius,
    coldSmoking:capability.coldSmoking,
    coldSmokeMaxCelsius:capability.coldSmokeMaxCelsius,
    temperatureControl:capability.temperatureControl,
    controlPrecisionCelsius:capability.controlPrecisionCelsius,
    capturedAt
  });
}

export function smokerSnapshotToJson(snapshot){
  const value=normaliseSmokerSnapshot(snapshot);
  return Object.freeze({
    ...value,
    fuelTypes:[...value.fuelTypes]
  });
}
